import React from 'react';
import { Link } from 'react-router-dom';
import BlogArticleTemplate from '../../components/BlogArticleTemplate';

const BorrowingPower = () => (
  <BlogArticleTemplate
    title="How Much Can You Borrow? Understanding Your Borrowing Power"
    description="Discover how lenders assess your borrowing capacity, what affects your serviceability, and practical steps to maximise how much you can borrow."
    path="/blog/borrowing-power"
    author="Manage Your Loans Team"
    date="2026-02-20"
    readTime={10}
    faqs={[
      {
        q: 'How do lenders calculate my borrowing power?',
        a: 'Lenders assess your borrowing power by analysing your gross income, existing debts and liabilities, living expenses (using the Household Expenditure Measure or your declared expenses — whichever is higher), the number of dependants, the loan term, and the interest rate plus a serviceability buffer (currently 3% above the product rate for most lenders). Each lender uses slightly different calculations, which is why borrowing capacities can vary significantly between institutions.',
      },
      {
        q: 'Why is my borrowing power different at different banks?',
        a: 'Each lender uses their own serviceability calculator with different assumptions about living expenses, income shading (how much of certain income types they count), and treatment of existing debts. Some lenders are more conservative while others take a more flexible approach to certain income sources like overtime, bonuses, or rental income. A mortgage broker can identify which lenders are most favourable for your specific income and expense profile.',
      },
      {
        q: 'Does having a credit card affect how much I can borrow?',
        a: 'Yes. Lenders assess credit card limits (not balances) as a potential liability. A credit card with a $10,000 limit is typically assessed as if you owe the full $10,000, regardless of your actual balance. This can reduce your borrowing power by $30,000 to $50,000 or more depending on the lender. Closing unused credit cards before applying for a home loan is one of the simplest ways to increase your borrowing capacity.',
      },
      {
        q: 'Can a mortgage broker help me borrow more?',
        a: 'A broker can help you optimise your borrowing position by identifying which lenders offer the most favourable assessment for your income type, advising on steps to increase your capacity (like closing unused credit facilities), and structuring your application to present your finances in the strongest possible way — all within responsible lending requirements. Brokers compare many lenders (the MYL panel has 80+), so they know which calculators produce the best results for different borrower profiles.',
      },
    ]}
  >
    <p className="text-lg text-gray-700 leading-relaxed mb-8">
      Understanding how much you can borrow is the foundation of any property
      purchase or refinance decision. Your borrowing power determines which
      properties are within your reach, how much you will repay each month, and
      the overall cost of your loan. This guide explains how lenders assess
      your capacity and what you can do to strengthen your position.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      What Is Borrowing Power?
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      Borrowing power (also called borrowing capacity) is the maximum amount a
      lender is willing to lend you based on your financial situation. It is
      determined by your income, expenses, debts, and the lender's own
      assessment criteria. Two people with identical incomes can have vastly
      different borrowing capacities depending on their expenses, existing debts,
      and which lender they approach.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      How Lenders Assess Your Borrowing Capacity
    </h2>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      Income Assessment
    </h3>
    <p className="text-gray-700 leading-relaxed mb-4">
      Lenders look at your gross income from all sources. For PAYG employees,
      this typically includes your base salary and may include regular overtime,
      bonuses, and commissions — though lenders often "shade" (discount)
      variable income components. For example, a lender might count 80% of
      overtime and 60% of bonus income.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      Self-employed borrowers are assessed differently. Lenders typically look
      at the last two years of tax returns and financial statements, calculating
      an average or using the lower of the two years. Some lenders accept one
      year of financials for established businesses. Your broker can identify
      which lenders take the most favourable view of self-employed income.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      Other income sources that may be considered include rental income (usually
      shaded to 80%), government payments (such as Family Tax Benefit),
      investment dividends, and salary sacrifice contributions. Not all lenders
      accept all income types, so the lender you choose matters.
    </p>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      Expense Assessment
    </h3>
    <p className="text-gray-700 leading-relaxed mb-4">
      Since the introduction of responsible lending obligations, lenders
      scrutinise expenses more carefully than ever. They will review your bank
      statements (typically the last three months) to verify your declared
      expenses. Lenders compare your declared expenses against the Household
      Expenditure Measure (HEM) and use the higher of the two.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      Common expenses that impact your borrowing power include rent, utilities,
      insurance premiums, school fees, childcare, subscriptions, dining out,
      and buy-now-pay-later commitments. Reducing discretionary spending in the
      months before applying can improve how your bank statements look to
      assessors.
    </p>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      Existing Debts and Liabilities
    </h3>
    <p className="text-gray-700 leading-relaxed mb-4">
      All existing debts reduce your borrowing power. This includes personal
      loans, car loans, HECS-HELP debts, credit cards, and buy-now-pay-later
      accounts. Credit cards are assessed on their limit, not their balance —
      a $15,000 credit card limit could reduce your borrowing capacity by
      $45,000 or more even if the card has a zero balance.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      HECS-HELP debt is factored into the assessment based on your repayment
      obligations relative to your income. While it cannot be "removed" from
      the assessment, understanding its impact helps you plan realistically.
    </p>

    <h3 className="text-xl font-semibold text-[#00594C] mt-8 mb-3">
      The Serviceability Buffer
    </h3>
    <p className="text-gray-700 leading-relaxed mb-4">
      Australian lenders are required to assess your ability to repay the loan
      at a rate higher than the actual product rate. This is called the
      serviceability buffer. As of early 2026, most lenders use a buffer of 3%
      above the product rate. This means if the loan rate is 6.00%, the lender
      assesses whether you can afford repayments at 9.00%.
    </p>
    <p className="text-gray-700 leading-relaxed mb-6">
      This buffer is set by the Australian Prudential Regulation Authority
      (APRA) and is designed to ensure borrowers can handle rate increases. It
      also means that the rate you are offered directly impacts your borrowing
      power — a lender offering a lower rate will generally approve a higher
      loan amount because the buffer is applied to a lower starting point.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      Practical Steps to Increase Your Borrowing Power
    </h2>
    <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
      <li className="leading-relaxed">
        <strong>Close unused credit cards and reduce limits.</strong> This is
        one of the most effective steps. Cancel cards you do not use and
        reduce limits on cards you keep to the minimum you need.
      </li>
      <li className="leading-relaxed">
        <strong>Pay down existing debts.</strong> Reducing personal loans,
        car loans, and buy-now-pay-later balances frees up serviceability
        and increases how much a lender will offer you.
      </li>
      <li className="leading-relaxed">
        <strong>Clean up your bank statements.</strong> Reduce discretionary
        spending in the three months before applying. Lenders review your
        statements, so consistently high spending on non-essentials can work
        against you.
      </li>
      <li className="leading-relaxed">
        <strong>Increase your income.</strong> A pay rise, additional work
        hours, or a second income source all improve your borrowing capacity.
        If you have recently received a raise, make sure you have at least
        one payslip reflecting the new salary before applying.
      </li>
      <li className="leading-relaxed">
        <strong>Extend the loan term.</strong> A 30-year loan term results in
        lower monthly repayments than a 25-year term, which can increase the
        amount a lender will approve. Discuss the trade-offs with your broker.
      </li>
      <li className="leading-relaxed">
        <strong>Choose the right lender.</strong> Different lenders produce
        different borrowing capacities for the same borrower. A{' '}
        <Link to="/blog/broker-vs-bank" className="text-[#00594C] font-semibold underline hover:text-[#99DF63] transition-colors">
          mortgage broker
        </Link>{' '}
        can run your scenario through multiple calculators to find the lender
        that offers the strongest result for your profile.
      </li>
    </ul>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      Borrowing Power with a Partner
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      Applying jointly generally increases your borrowing power because two
      incomes are assessed. However, both applicants' debts and expenses are
      also factored in. If one partner has significant debts or a poor credit
      history, it may in some cases be more effective for the stronger
      applicant to apply individually — though this depends on the specific
      circumstances and lender policies.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      Using a Guarantor
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      A family guarantor can use the equity in their own property to support
      your loan application. This does not necessarily increase your borrowing
      power (which is based on your ability to service the loan), but it can
      eliminate the need for a full deposit and avoid LMI. Guarantor
      arrangements should be carefully considered, and we recommend both
      parties seek independent legal advice.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      Learn more about how we can help assess your borrowing power through our{' '}
      <Link to="/first-home-buyers" className="text-[#00594C] font-semibold underline hover:text-[#99DF63] transition-colors">
        first home buyer
      </Link>{' '}
      or{' '}
      <Link to="/next-home" className="text-[#00594C] font-semibold underline hover:text-[#99DF63] transition-colors">
        next home
      </Link>{' '}
      services. Our brokers offer a free, no-obligation borrowing assessment
      to help you understand exactly where you stand.
    </p>
  </BlogArticleTemplate>
);

export default BorrowingPower;
